import React, { useCallback, useEffect, useState } from 'react';
import {
  FaShieldAlt,
  FaExclamationTriangle,
  FaSpinner,
  FaSearch,
  FaEye,
  FaUndo,
  FaBan,
  FaUserSlash,
  FaFlag,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { riskService } from '../../../../services/riskService';

const STATE_BADGE = {
  active: 'bg-emerald-100 text-emerald-800',
  soft_suspended: 'bg-amber-100 text-amber-800',
  hard_suspended: 'bg-red-100 text-red-800',
  permanently_banned: 'bg-slate-800 text-white',
};

function formatState(state) {
  return (state || 'active').replace(/_/g, ' ');
}

const RiskDashboard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSellerId, setSelectedSellerId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const data = await riskService.listDashboard({
        state: stateFilter || undefined,
        flaggedOnly: flaggedOnly ? 'true' : 'false',
        limit: 100,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load risk dashboard');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [stateFilter, flaggedOnly]);

  const loadSellerDetail = useCallback(async (sellerId) => {
    if (!sellerId) return;
    try {
      setDetailLoading(true);
      const data = await riskService.getSellerRisk(sellerId);
      setDetail(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load seller risk profile');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (selectedSellerId) loadSellerDetail(selectedSellerId);
  }, [selectedSellerId, loadSellerDetail]);

  const filteredRows = rows.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name =
      row.seller?.vendorDetails?.businessName ||
      row.seller?.name ||
      row.seller?.email ||
      '';
    return String(row.sellerId || '').toLowerCase().includes(q) || name.toLowerCase().includes(q);
  });

  const openSeller = (sellerId) => {
    setSelectedSellerId(sellerId);
    setDetail(null);
  };

  const runAction = async (action) => {
    if (!selectedSellerId) return;
    const reason = window.prompt('Reason (optional):') || '';
    try {
      setActionLoading(true);
      if (action === 'clear') {
        await riskService.clearSuspension(selectedSellerId, reason);
        toast.success('Suspension cleared');
      } else if (action === 'hard') {
        await riskService.hardSuspend(selectedSellerId, { reason });
        toast.success('Hard suspension applied');
      } else if (action === 'ban') {
        const confirmedFraud = window.confirm('Mark as confirmed fraud?');
        await riskService.permanentBan(selectedSellerId, { reason, confirmedFraud });
        toast.success('Permanent ban applied');
      }
      await loadDashboard();
      await loadSellerDetail(selectedSellerId);
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FaShieldAlt className="text-red-600" />
            Risk & Suspension
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Server-enforced suspension state, strike counters, and fraud event log.
          </p>
        </div>
        <button
          type="button"
          onClick={loadDashboard}
          className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium"
        >
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search seller name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
          />
        </div>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
        >
          <option value="">All states</option>
          <option value="active">Active</option>
          <option value="soft_suspended">Soft suspended</option>
          <option value="hard_suspended">Hard suspended</option>
          <option value="permanently_banned">Permanently banned</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={flaggedOnly}
            onChange={(e) => setFlaggedOnly(e.target.checked)}
          />
          Flagged only
        </label>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 dashboard-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <FaSpinner className="animate-spin mr-2" /> Loading...
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="py-16 text-center text-slate-500">No sellers match filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Seller</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">State</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">SLA strikes</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Flags</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const sellerId = row.sellerId?._id || row.sellerId;
                    const state = row.state || row.suspension?.state || 'active';
                    const strikes =
                      row.strikeSummary?.activeSlaStrikes ??
                      row.riskSummary?.activeSlaStrikes ??
                      0;
                    const flags = row.riskSummary || row.flags || {};
                    return (
                      <tr
                        key={String(sellerId)}
                        className={`border-b border-slate-100 hover:bg-slate-50 ${
                          selectedSellerId === String(sellerId) ? 'bg-red-50' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">
                            {row.seller?.vendorDetails?.businessName ||
                              row.seller?.name ||
                              'Unknown seller'}
                          </div>
                          <div className="text-xs text-slate-500 truncate max-w-[200px]">
                            {sellerId}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                              STATE_BADGE[state] || STATE_BADGE.active
                            }`}
                          >
                            {formatState(state)}
                          </span>
                        </td>
                        <td className="px-4 py-3">{strikes}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {flags.accountReviewFlag && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                                Review
                              </span>
                            )}
                            {flags.listingsPaused && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                Paused
                              </span>
                            )}
                            {flags.oemPermissionRevoked && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                OEM revoked
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => openSeller(String(sellerId))}
                            className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            <FaEye /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="dashboard-card">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FaFlag className="text-amber-500" />
            Seller detail
          </h2>
          {!selectedSellerId ? (
            <p className="text-sm text-slate-500">Select a seller to view events and audit log.</p>
          ) : detailLoading ? (
            <div className="flex items-center text-slate-500 py-8">
              <FaSpinner className="animate-spin mr-2" /> Loading...
            </div>
          ) : detail ? (
            <div className="space-y-4 text-sm">
              <div>
                <div className="font-semibold text-slate-900">{detail.seller?.businessName || detail.seller?.name}</div>
                <div className="text-slate-500">{detail.seller?.email}</div>
                <span
                  className={`inline-flex mt-2 px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                    STATE_BADGE[detail.suspension?.state] || STATE_BADGE.active
                  }`}
                >
                  {formatState(detail.suspension?.state)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="text-xs text-slate-500">SLA strikes (30d)</div>
                  <div className="text-lg font-bold">
                    {detail.strikeSummary?.activeSlaStrikes ?? 0}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <div className="text-xs text-slate-500">HIGH events (30d)</div>
                  <div className="text-lg font-bold">
                    {detail.riskSummary?.activeHighEvents ?? 0}
                  </div>
                </div>
              </div>

              {detail.suspension?.suspensionReason && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-amber-900">
                  <FaExclamationTriangle className="inline mr-1" />
                  {detail.suspension.suspensionReason}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={actionLoading || detail.suspension?.state === 'active'}
                  onClick={() => runAction('clear')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium disabled:opacity-50"
                >
                  <FaUndo /> Clear suspension
                </button>
                <button
                  type="button"
                  disabled={actionLoading || detail.suspension?.state === 'hard_suspended'}
                  onClick={() => runAction('hard')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium disabled:opacity-50"
                >
                  <FaBan /> Hard suspend
                </button>
                <button
                  type="button"
                  disabled={actionLoading || detail.suspension?.state === 'permanently_banned'}
                  onClick={() => runAction('ban')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-medium disabled:opacity-50"
                >
                  <FaUserSlash /> Permanent ban
                </button>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Recent fraud events</h3>
                <ul className="space-y-2 max-h-40 overflow-y-auto">
                  {(detail.recentEvents || []).slice(0, 8).map((ev) => (
                    <li key={ev.eventId || ev._id} className="border-b border-slate-100 pb-2">
                      <span className="font-mono text-xs text-red-600">{ev.gateCode}</span>
                      <span className="ml-2 text-xs text-slate-500">{ev.severity}</span>
                      <div className="text-xs text-slate-600 truncate">{ev.action || JSON.stringify(ev.detail)}</div>
                    </li>
                  ))}
                  {!detail.recentEvents?.length && (
                    <li className="text-slate-500 text-xs">No events logged.</li>
                  )}
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Audit log</h3>
                <ul className="space-y-2 max-h-40 overflow-y-auto">
                  {(detail.recentAudits || []).slice(0, 8).map((log) => (
                    <li key={log.auditId || log._id} className="border-b border-slate-100 pb-2 text-xs">
                      <span className="font-medium">{log.action}</span>
                      <span className="text-slate-500 ml-1">
                        {log.fromState} → {log.toState}
                      </span>
                      <div className="text-slate-500">
                        {log.actorType} · {new Date(log.createdAt).toLocaleString()}
                      </div>
                    </li>
                  ))}
                  {!detail.recentAudits?.length && (
                    <li className="text-slate-500 text-xs">No audit entries.</li>
                  )}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RiskDashboard;
